import { Component, ElementRef, Injector, OnDestroy, ViewChild, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ScrollTrigger, gsap } from '../../../../../motion/gsap';
import { QUERIES } from '../../../../../motion/motion-env';
import { MotionService } from '../../../../../motion/motion.service';
import { translateLive } from '../../../../../services/i18n-live';

interface FeatureItem {
  title: string;
  desc: string;
}

interface Point {
  x: number;
  y: number;
}

interface Wire {
  item: HTMLElement;
  side: 'l' | 'r';
  path: SVGPathElement;
  dot: SVGCircleElement;
}

type SafeFn = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => void;

const SVG_NS = 'http://www.w3.org/2000/svg';
const PATH_LENGTH = 1000;
/** Outer radius of the logomark ring as a fraction of half the SVG width (the viewBox is tight). */
const RIM = 1;
/** px left between the rim and the wire tip; covers the 0.75px round cap, so it never touches the mark. */
const RIM_GAP = 1.5;
/** px left between an item's text edge and its wire. */
const TEXT_GAP = 8;
/** s; when the first wire starts, once the icon has mostly settled. */
const WIRES_AT = 0.6;
const WIRE_STAGGER = 0.12;
const WIRE_DURATION = 0.7;
/** Resting opacity of the glow when it breathes (CSS keyframes start here). */
const GLOW_FROM = 0.6;

/**
 * Home #features: on desktop the official icon powers up and thin "wires" run from its rim to
 * each feature, which rises as its wire arrives. Below 992px the items simply fade up.
 * Items arrive asynchronously (translations), so the animation context is (re)built once they
 * are rendered; a language switch only redraws the wires.
 */
@Component({
  selector: 'app-home-features',
  templateUrl: './home-features.component.html',
  styleUrl: './home-features.component.css',
})
export class HomeFeaturesSectionComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly motion = inject(MotionService);
  private readonly injector = inject(Injector);

  @ViewChild('section', { static: true }) private sectionRef!: ElementRef<HTMLElement>;
  @ViewChild('row', { static: true }) private rowRef!: ElementRef<HTMLElement>;
  @ViewChild('hub', { static: true }) private hubRef!: ElementRef<HTMLElement>;
  @ViewChild('wires', { static: true }) private wiresRef!: ElementRef<SVGSVGElement>;

  leftServices: FeatureItem[] = [];
  rightServices: FeatureItem[] = [];

  private mm?: ReturnType<typeof gsap.matchMedia>;
  private builtFor = 0;
  /** The entrance has played (in either branch); a rebuilt branch then starts in its final state. */
  private played = false;
  private syncQueued = false;
  private destroyed = false;
  /** Set while the desktop branch is active. */
  private redraw: (() => void) | null = null;
  private frame = 0;
  private resizeObserver?: ResizeObserver;
  private visibilityObserver?: IntersectionObserver;

  constructor() {
    translateLive<FeatureItem[]>(this.translate, 'leftServices')
      .pipe(takeUntilDestroyed())
      .subscribe((services) => {
        this.leftServices = services;
        this.queueSync();
      });
    translateLive<FeatureItem[]>(this.translate, 'rightServices')
      .pipe(takeUntilDestroyed())
      .subscribe((services) => {
        this.rightServices = services;
        this.queueSync();
      });
    this.queueSync();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.mm?.revert();
    this.mm = undefined;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** Runs once the DOM reflects the latest data; both lists emitting together share one sync. */
  private queueSync(): void {
    if (!this.motion.isBrowser || this.syncQueued) return;
    this.syncQueued = true;
    afterNextRender(
      () => {
        this.syncQueued = false;
        if (!this.destroyed) this.motion.outside(() => this.sync());
      },
      { injector: this.injector }
    );
  }

  private sync(): void {
    this.observe();
    const count = this.items().length;
    if (count === this.builtFor && this.mm) {
      this.redraw?.();
      return;
    }
    this.mm?.revert();
    this.mm = undefined;
    this.builtFor = count;
    if (!count) return;

    const mm = (this.mm = gsap.matchMedia(this.sectionRef.nativeElement));
    mm.add({ motion: QUERIES.motion, desktop: QUERIES.desktop }, (ctx, contextSafe) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion']) return;
      const safe: SafeFn = (fn) => contextSafe!(fn) as typeof fn;
      return conditions['desktop'] ? this.buildDesktop(safe) : this.buildCompact(safe);
    });
  }

  /** Desktop (>= 992px): wires from the icon rim to each item, L1 R1 L2 R2 L3 R3. */
  private buildDesktop(safe: SafeFn): () => void {
    const row = this.rowRef.nativeElement;
    const hub = this.hubRef.nativeElement;
    const svg = this.wiresRef.nativeElement;
    const icon = hub.querySelector<HTMLElement>('.feature-img')!;
    const glow = hub.querySelector<HTMLElement>('.feat-glow')!;

    const wires: Wire[] = this.orderedItems().map((item) => {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('class', 'feat-wire');
      // Normalised length: the dash maths stays valid when the geometry is recomputed mid-build.
      // 1000 rather than 1 because GSAP rounds px values to whole numbers while tweening.
      path.setAttribute('pathLength', String(PATH_LENGTH));
      const dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('class', 'feat-dot');
      dot.setAttribute('r', '2.5');
      svg.append(path, dot);
      return { item, side: item.dataset['side'] === 'r' ? 'r' : 'l', path, dot };
    });

    // Rects of elements that are never transformed (row, hub, item boxes; only the icon and the
    // items' children move), so the geometry is exact even mid-build or mid-float.
    const draw = () => {
      const rowRect = row.getBoundingClientRect();
      const hubRect = hub.getBoundingClientRect();
      const iconWidth = parseFloat(getComputedStyle(icon).width);
      if (!iconWidth || !hubRect.height) return;
      // The icon is a centred block that fills the hub's height.
      const c: Point = {
        x: hubRect.left - rowRect.left + hubRect.width / 2,
        y: hubRect.top - rowRect.top + hubRect.height / 2,
      };
      const radius = (iconWidth / 2) * RIM + RIM_GAP;
      for (const wire of wires) {
        const b = wire.item.getBoundingClientRect();
        // Midpoint of the item's inner edge (the side facing the hub).
        const s: Point = {
          x: (wire.side === 'l' ? b.right + TEXT_GAP : b.left - TEXT_GAP) - rowRect.left,
          y: b.top + b.height / 2 - rowRect.top,
        };
        const dx = s.x - c.x;
        const dy = s.y - c.y;
        const len = Math.hypot(dx, dy) || 1;
        const e: Point = { x: c.x + (radius * dx) / len, y: c.y + (radius * dy) / len };
        const c1: Point = { x: s.x + (e.x - s.x) * 0.5, y: s.y };
        const c2: Point = { x: e.x - (e.x - s.x) * 0.3, y: e.y };
        // Same curve as item -> rim, written rim -> item so the current flows out to the feature.
        const d = `M${r(e.x)} ${r(e.y)}C${r(c2.x)} ${r(c2.y)} ${r(c1.x)} ${r(c1.y)} ${r(s.x)} ${r(s.y)}`;
        wire.path.setAttribute('d', d);
        wire.dot.style.offsetPath = `path("${d}")`;
      }
    };
    draw();
    this.redraw = draw;
    this.motion.fontsReady.then(() => this.redraw?.());

    const cleanup = () => {
      this.redraw = null;
      svg.replaceChildren();
      this.setBuilt(false);
    };

    if (this.played || this.skipEntrance()) {
      this.played = true;
      this.setBuilt(true);
      return cleanup;
    }

    const paths = wires.map((w) => w.path);
    const dots = wires.map((w) => w.dot);
    gsap.set(icon, { opacity: 0, scale: 0.92 });
    gsap.set(glow, { opacity: 0 });
    gsap.set(paths, { strokeDasharray: `${PATH_LENGTH} ${PATH_LENGTH * 2}`, strokeDashoffset: PATH_LENGTH });
    gsap.set(dots, { opacity: 0, offsetDistance: '0%' });
    for (const wire of wires) {
      gsap.set(this.texts(wire.item), { opacity: 0, y: 16 });
      const separator = wire.item.querySelector('.custom-separator');
      if (separator) gsap.set(separator, { scaleX: 0, transformOrigin: wire.side === 'l' ? '100% 50%' : '0% 50%' });
    }

    const tl = gsap.timeline({
      scrollTrigger: { trigger: row, start: 'top 70%', once: true },
      onComplete: safe(() => {
        this.played = true;
        gsap.set(paths, { clearProps: 'strokeDasharray,strokeDashoffset' });
        gsap.set(glow, { clearProps: 'opacity' });
        this.setBuilt(true);
      }),
    });
    tl.to(icon, { opacity: 1, scale: 1, duration: 0.8, ease: 'expo.out', clearProps: 'opacity,transform' }, 0);
    tl.to(glow, { opacity: GLOW_FROM, duration: 0.8, ease: 'power1.out' }, 0);
    wires.forEach((wire, i) => {
      const at = WIRES_AT + i * WIRE_STAGGER;
      const arrive = at + WIRE_DURATION;
      // Same ease for line and dot, so the dot rides the tip of the wire as it draws.
      tl.to(wire.path, { strokeDashoffset: 0, duration: WIRE_DURATION, ease: 'power2.inOut' }, at);
      tl.set(wire.dot, { opacity: 1 }, at);
      tl.to(wire.dot, { offsetDistance: '100%', duration: WIRE_DURATION, ease: 'power2.inOut' }, at);
      tl.to(wire.dot, { opacity: 0, duration: 0.2, ease: 'power1.out' }, arrive);
      tl.to(this.texts(wire.item), { opacity: 1, y: 0, duration: 0.5, clearProps: 'opacity,transform' }, arrive - 0.1);
      const separator = wire.item.querySelector('.custom-separator');
      if (separator) tl.to(separator, { scaleX: 1, duration: 0.5, ease: 'power2.out', clearProps: 'transform' }, arrive - 0.1);
    });

    return cleanup;
  }

  /** Below 992px: no wires; items fade up in batches and the icon fades in. */
  private buildCompact(safe: SafeFn): () => void {
    const hub = this.hubRef.nativeElement;
    const icon = hub.querySelector<HTMLElement>('.feature-img')!;
    const glow = hub.querySelector<HTMLElement>('.feat-glow')!;
    const cleanup = () => this.setBuilt(false);

    if (this.played || this.skipEntrance()) {
      this.played = true;
      this.setBuilt(true);
      return cleanup;
    }

    const items = this.items();
    gsap.set(items, { opacity: 0, y: 16 });
    gsap.set([icon, glow], { opacity: 0 });
    ScrollTrigger.batch(items, {
      start: 'top 88%',
      once: true,
      onEnter: safe((batch: Element[]) => {
        gsap.to(batch, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, clearProps: 'opacity,transform' });
      }),
    });
    gsap
      .timeline({
        scrollTrigger: { trigger: icon, start: 'top 88%', once: true },
        onComplete: safe(() => {
          this.played = true;
          gsap.set(glow, { clearProps: 'opacity' });
          this.setBuilt(true);
        }),
      })
      .to(icon, { opacity: 1, duration: 0.8, ease: 'power1.out', clearProps: 'opacity' }, 0)
      .to(glow, { opacity: GLOW_FROM, duration: 0.8, ease: 'power1.out' }, 0);

    return cleanup;
  }

  /** Watches layout (resize, reflow after fonts/images/text) and visibility. Idempotent. */
  private observe(): void {
    if (this.resizeObserver) return;
    const section = this.sectionRef.nativeElement;
    const row = this.rowRef.nativeElement;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.redraw?.();
        });
      });
      this.resizeObserver.observe(row);
      for (const col of Array.from(row.children)) {
        if (col instanceof HTMLElement) this.resizeObserver.observe(col);
      }
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.visibilityObserver = new IntersectionObserver(([entry]) =>
        section.classList.toggle('is-paused', !entry.isIntersecting)
      );
      this.visibilityObserver.observe(section);
    }
  }

  /**
   * No entrance when the row is already scrolled past, or when the page was just re-rendered by a
   * language switch with the section on screen (same rule as RevealDirective).
   */
  private skipEntrance(): boolean {
    const rect = this.rowRef.nativeElement.getBoundingClientRect();
    return rect.bottom <= 0 || (this.motion.recentLanguageSwitch() && rect.top < window.innerHeight);
  }

  private items(): HTMLElement[] {
    return Array.from(this.rowRef.nativeElement.querySelectorAll<HTMLElement>('.feat-item'));
  }

  /** L1, R1, L2, R2, ... */
  private orderedItems(): HTMLElement[] {
    const items = this.items();
    const left = items.filter((el) => el.dataset['side'] !== 'r');
    const right = items.filter((el) => el.dataset['side'] === 'r');
    const ordered: HTMLElement[] = [];
    for (let i = 0; i < Math.max(left.length, right.length); i++) {
      if (left[i]) ordered.push(left[i]);
      if (right[i]) ordered.push(right[i]);
    }
    return ordered;
  }

  private texts(item: HTMLElement): Element[] {
    return Array.from(item.querySelectorAll(':scope > h5, :scope > p'));
  }

  /**
   * Starts (or stops) the idle float. The wire overlay floats with the icon, on the same keyframes
   * from the same frame, so the wire tips keep their distance to the rim.
   */
  private setBuilt(on: boolean): void {
    this.hubRef.nativeElement.classList.toggle('is-built', on);
    this.wiresRef.nativeElement.classList.toggle('is-built', on);
  }
}

function r(n: number): number {
  return Math.round(n * 10) / 10;
}
